import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Star,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  CheckCircle2,
  MapPin,
  Quote,
  Wrench,
  Sparkles,
  MoveHorizontal,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';

export interface Testimonial {
  id: string;
  customerName: string;
  avatarUrl?: string;
  location: string;
  state: string;
  serviceCategory: string;
  serviceTitle: string;
  artisanName: string;
  rating: number;
  reviewText: string;
  completedDate: string;
  jobAmount: string;
  verifiedBadge: boolean;
}

const TESTIMONIALS: Testimonial[] = [
  {
    id: 'test-1',
    customerName: 'Mrs. Folashade Adeyemi',
    location: 'Lekki Phase 1',
    state: 'Lagos',
    serviceCategory: 'Electrical Services',
    serviceTitle: 'Complete DB Board & Inverter Electrical Rewiring',
    artisanName: 'Engr. Babatunde O.',
    rating: 5,
    reviewText:
      'I was extremely skeptical about finding a reliable electrician in Lekki after previous bad experiences with cut corners. JobMatch NG matched me with Babatunde within 2 hours. His quote was completely transparent with materials and labor broken down, and the escrow system gave me 100% peace of mind. Exceptional workmanship and neat cabling!',
    completedDate: 'October 2026',
    jobAmount: '₦85,000',
    verifiedBadge: true,
  },
  {
    id: 'test-2',
    customerName: 'Dr. Chukwuma Eze',
    location: 'Garki 2',
    state: 'Abuja FCT',
    serviceCategory: 'Plumbing & Drainage',
    serviceTitle: 'Emergency Concealed Pipe Leak & Pressure Valve Repair',
    artisanName: 'Samuel N.',
    rating: 5,
    reviewText:
      'Our kitchen wall was soaking through from an internal pipe crack on a Sunday morning. Samuel diagnosed it accurately via video upload before driving over with exact PPR fittings. Repaired in under three hours without breaking down unnecessary tiles. Highest recommendation for professionalism.',
    completedDate: 'October 2026',
    jobAmount: '₦45,000',
    verifiedBadge: true,
  },
  {
    id: 'test-3',
    customerName: 'Amina Bello-Danjuma',
    location: 'GRA Phase 2',
    state: 'Port Harcourt',
    serviceCategory: 'Air Conditioning & Cooling',
    serviceTitle: 'Dual Inverter Split AC Installation & Vacuum Testing',
    artisanName: 'Chidi K.',
    rating: 5,
    reviewText:
      'Chidi arrived on time, wore protective shoe covers indoors, and used genuine copper piping and a digital manifold gauge to check vacuum pressure. No dust was left behind. Releasing funds via Paystack escrow only after testing the cooling performance made the entire transaction stress-free.',
    completedDate: 'September 2026',
    jobAmount: '₦60,000',
    verifiedBadge: true,
  },
  {
    id: 'test-4',
    customerName: 'Tunde Balogun',
    location: 'Ikeja GRA',
    state: 'Lagos',
    serviceCategory: 'Generator & Power Systems',
    serviceTitle: '15kVA Diesel Generator Overhaul & Alternator Servicing',
    artisanName: 'Haruna M.',
    rating: 5,
    reviewText:
      'Finding an honest generator technician in Lagos who does not inflate spare parts prices is rare. Haruna arrived with original filters, produced verifiable vendor invoices, and thoroughly load-tested the plant before requesting sign-off. Our generator now runs quieter and cooler.',
    completedDate: 'October 2026',
    jobAmount: '₦120,000',
    verifiedBadge: true,
  },
];

export const VerifiedCustomerTestimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Gesture tracking refs
  const startXRef = useRef<number>(0);
  const startYRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const currentXRef = useRef<number>(0);
  const isPointerDownRef = useRef<boolean>(false);
  const isDirectionLockedRef = useRef<boolean>(false);
  const isHorizontalSwipeRef = useRef<boolean>(false);
  const hapticTriggeredRef = useRef<boolean>(false);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const total = TESTIMONIALS.length;

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % total);
  }, [total]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  // Autoplay functionality with pause on hover/drag
  useEffect(() => {
    if (isPaused || isDragging) {
      if (autoplayTimerRef.current) clearInterval(autoplayTimerRef.current);
      return;
    }
    autoplayTimerRef.current = setInterval(() => {
      handleNext();
    }, 6500);

    return () => {
      if (autoplayTimerRef.current) clearInterval(autoplayTimerRef.current);
    };
  }, [isPaused, isDragging, handleNext]);

  // Handle gesture start (both touch and pointer)
  const handleDragStart = (clientX: number, clientY: number) => {
    isPointerDownRef.current = true;
    startXRef.current = clientX;
    startYRef.current = clientY;
    currentXRef.current = clientX;
    startTimeRef.current = Date.now();
    isDirectionLockedRef.current = false;
    isHorizontalSwipeRef.current = false;
    hapticTriggeredRef.current = false;
    setIsPaused(true);
  };

  // Handle gesture move with smart vertical-scroll vs horizontal-swipe locking
  const handleDragMove = (clientX: number, clientY: number, cancelable?: boolean, preventDefault?: () => void) => {
    if (!isPointerDownRef.current) return;
    currentXRef.current = clientX;

    const deltaX = clientX - startXRef.current;
    const deltaY = clientY - startYRef.current;

    // Detect gesture direction intent within the first ~6px of motion
    if (!isDirectionLockedRef.current) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (absX > 6 || absY > 6) {
        if (absX >= absY) {
          // Locked to horizontal swipe
          isHorizontalSwipeRef.current = true;
          isDirectionLockedRef.current = true;
          setIsDragging(true);
        } else {
          // Locked to vertical scroll; do not hijack page scrolling
          isHorizontalSwipeRef.current = false;
          isDirectionLockedRef.current = true;
        }
      }
    }

    if (isHorizontalSwipeRef.current) {
      if (cancelable && preventDefault) {
        preventDefault();
      }

      // Elastic resistance formula for organic feel
      const dampening = 0.88;
      const computedOffset = deltaX * dampening;
      setDragOffset(computedOffset);

      // Trigger subtle tactile haptic vibration when crossing 45px threshold once
      if (Math.abs(computedOffset) >= 45 && !hapticTriggeredRef.current) {
        hapticTriggeredRef.current = true;
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          try {
            navigator.vibrate(8);
          } catch {
            // Safe fallback if unsupported or unpermitted
          }
        }
      }
    }
  };

  // Handle gesture end / release
  const handleDragEnd = () => {
    if (!isPointerDownRef.current) return;
    isPointerDownRef.current = false;

    if (isHorizontalSwipeRef.current) {
      const elapsed = Math.max(1, Date.now() - startTimeRef.current);
      const distance = currentXRef.current - startXRef.current;
      const velocity = Math.abs(distance) / elapsed; // px per ms

      const isFastFlick = velocity > 0.32 && Math.abs(distance) > 25;
      const isPastDistanceThreshold = Math.abs(dragOffset) > 48;

      if (isFastFlick || isPastDistanceThreshold) {
        if (distance < 0 || dragOffset < 0) {
          // Swiped Left -> Advance to Next Testimonial
          handleNext();
        } else {
          // Swiped Right -> Retreat to Previous Testimonial
          handlePrev();
        }
      }
    }

    // Reset interaction states smoothly
    setIsDragging(false);
    setDragOffset(0);
    isHorizontalSwipeRef.current = false;
    isDirectionLockedRef.current = false;

    // Resume autoplay after 1.5s idle
    setTimeout(() => {
      setIsPaused(false);
    }, 1500);
  };

  // Pointer event handlers (Desktop mouse drag & unified pointer)
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    handleDragStart(e.clientX, e.clientY);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const onPointerUp = () => {
    handleDragEnd();
  };

  const onPointerCancel = () => {
    handleDragEnd();
  };

  // Native Touch event handlers (Mobile Safari & Android WebViews)
  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 1) {
      handleDragMove(
        e.touches[0].clientX,
        e.touches[0].clientY,
        e.cancelable,
        () => e.preventDefault()
      );
    }
  };

  const onTouchEnd = () => {
    handleDragEnd();
  };

  // Keyboard navigation for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNext();
    }
  };

  const activeTestimonial = TESTIMONIALS[currentIndex];

  // Calculated gesture transforms
  const isSwipingLeft = dragOffset < -15;
  const isSwipingRight = dragOffset > 15;
  const rotationAngle = dragOffset * 0.012; // subtle organic tilt while dragging
  const cardOpacity = isDragging ? Math.max(0.8, 1 - Math.abs(dragOffset) / 800) : 1;

  return (
    <section
      aria-label="Verified Customer Testimonials"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 text-white rounded-3xl p-6 sm:p-10 lg:p-12 border border-slate-800 shadow-xl relative overflow-hidden">
        {/* Subtle decorative radial glow */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-emerald-600/5 rounded-full blur-2xl pointer-events-none" />

        {/* Section Header */}
        <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 sm:mb-10 pb-6 border-b border-slate-800/80">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>100% Escrow-Protected Workmanship</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
              What Verified Nigerian Customers Are Saying
            </h2>
            <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
              Real feedback from homeowners and businesses who booked, inspected, and paid certified artisans through JobMatch NG.
            </p>
          </div>

          {/* Navigation Controls & Mobile Hint */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Mobile swipe gesture hint */}
            <div className="flex sm:hidden items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/80 border border-slate-700/60 text-slate-300 text-[11px] font-medium">
              <MoveHorizontal className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
              <span>Swipe card left/right</span>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400 font-medium tabular-nums mr-1">
                <span className="text-white font-bold">{currentIndex + 1}</span> of {total}
              </span>
              <button
                type="button"
                onClick={handlePrev}
                aria-label="Previous testimonial"
                className="w-10 h-10 rounded-xl bg-slate-800/90 hover:bg-slate-700 active:bg-slate-600 text-slate-200 border border-slate-700/80 flex items-center justify-center transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                aria-label="Next testimonial"
                className="w-10 h-10 rounded-xl bg-slate-800/90 hover:bg-slate-700 active:bg-slate-600 text-slate-200 border border-slate-700/80 flex items-center justify-center transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Swipeable Carousel Presentation Container */}
        <div
          role="region"
          aria-roledescription="carousel"
          aria-label="Verified Customer Testimonials Carousel"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={`relative touch-pan-y select-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50 rounded-2xl ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
        >
          {/* Real-time Dynamic Swipe Direction Cues */}
          {isSwipingLeft && (
            <div className="absolute -top-3 right-4 z-20 hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-slate-950 font-bold text-xs shadow-lg animate-in fade-in duration-150">
              <span>Next Review</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          )}
          {isSwipingRight && (
            <div className="absolute -top-3 left-4 z-20 hidden sm:flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500 text-slate-950 font-bold text-xs shadow-lg animate-in fade-in duration-150">
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Previous Review</span>
            </div>
          )}

          {/* Main Active Testimonial Card with Real-Time Gesture Offset */}
          <div
            style={{
              transform: `translateX(${dragOffset}px) rotate(${rotationAngle}deg)`,
              transition: isDragging
                ? 'none'
                : 'transform 360ms cubic-bezier(0.16, 1, 0.3, 1), opacity 280ms ease',
              opacity: cardOpacity,
            }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch will-change-transform"
          >
            {/* Left Column: Quote Details & Customer Identity */}
            <div className="lg:col-span-8 flex flex-col justify-between bg-slate-850/60 bg-slate-900/90 rounded-2xl p-6 sm:p-8 border border-slate-800/90 backdrop-blur-xs relative shadow-md">
              <div className="space-y-4">
                {/* 5-Star Rating & Service Tag */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-1.5" aria-label={`Rating: ${activeTestimonial.rating} out of 5 stars`}>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 fill-amber-400 text-amber-400"
                        aria-hidden="true"
                      />
                    ))}
                    <span className="text-xs font-bold text-amber-300 ml-1">5.0</span>
                  </div>

                  <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400 font-semibold bg-emerald-950/60 border border-emerald-800/40 px-2.5 py-1 rounded-lg">
                    <Wrench className="w-3 h-3 text-emerald-400" />
                    <span>{activeTestimonial.serviceCategory}</span>
                  </div>
                </div>

                {/* Feedback Quote */}
                <div className="relative pt-2">
                  <Quote className="w-8 h-8 text-emerald-500/20 absolute -top-1 -left-2 pointer-events-none" />
                  <p className="text-sm sm:text-base text-slate-200 font-normal leading-relaxed italic relative z-10 pl-4 border-l-2 border-emerald-500/60">
                    "{activeTestimonial.reviewText}"
                  </p>
                </div>
              </div>

              {/* Customer Author Info */}
              <div className="pt-6 mt-6 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-emerald-600 to-teal-500 text-white font-bold text-sm flex items-center justify-center shadow-inner">
                    {activeTestimonial.customerName
                      .split(' ')
                      .map((n) => n[0])
                      .filter((_, idx, arr) => idx === 0 || idx === arr.length - 1)
                      .join('')}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-bold text-sm text-white">
                        {activeTestimonial.customerName}
                      </h4>
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                        <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                        <span>Verified Customer</span>
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3 text-slate-500 shrink-0" />
                      <span>
                        {activeTestimonial.location}, {activeTestimonial.state}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="text-right text-xs text-slate-400">
                  <span className="block text-[11px] text-slate-500">Service Date</span>
                  <span className="font-medium text-slate-300">{activeTestimonial.completedDate}</span>
                </div>
              </div>
            </div>

            {/* Right Column: Verified Job Card & Escrow Summary */}
            <div className="lg:col-span-4 flex flex-col justify-between bg-slate-800/40 rounded-2xl p-6 border border-slate-800 space-y-4">
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-300">
                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Job & Escrow Breakdown</span>
                </div>

                <div className="bg-slate-900/80 rounded-xl p-3.5 border border-slate-800 space-y-2">
                  <span className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold block">
                    Completed Task
                  </span>
                  <p className="text-xs font-semibold text-slate-200 leading-snug">
                    {activeTestimonial.serviceTitle}
                  </p>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800">
                    <span className="text-slate-400">Assigned Professional</span>
                    <span className="font-semibold text-white">{activeTestimonial.artisanName}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800">
                    <span className="text-slate-400">Total Escrow Value</span>
                    <span className="font-bold text-emerald-400">{activeTestimonial.jobAmount}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-slate-400">Payment Protection</span>
                    <span className="font-medium text-slate-300 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      Paystack Escrow
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-800">
                <div className="text-[11px] text-slate-400 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Payment released only after customer inspection and milestone sign-off.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick-Jump Pagination Dots & Thumbnail Carousel Previews */}
          <div className="mt-8 pt-6 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Dots with generous touch targets for mobile fingers */}
            <div className="flex items-center gap-1" role="tablist" aria-label="Testimonial slides">
              {TESTIMONIALS.map((test, index) => {
                const isActive = index === currentIndex;
                return (
                  <button
                    key={test.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`Go to slide ${index + 1}: ${test.customerName}`}
                    onClick={() => setCurrentIndex(index)}
                    className="p-2 flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded-full"
                  >
                    <span
                      className={`block h-2.5 rounded-full transition-all duration-300 ${
                        isActive ? 'w-8 bg-emerald-500 shadow-sm shadow-emerald-500/50' : 'w-2.5 bg-slate-700 hover:bg-slate-500'
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {/* Quick-selector cards for larger screens */}
            <div className="hidden md:flex items-center gap-2">
              {TESTIMONIALS.map((test, index) => {
                const isActive = index === currentIndex;
                return (
                  <button
                    key={test.id}
                    type="button"
                    onClick={() => setCurrentIndex(index)}
                    className={`text-left px-3 py-1.5 rounded-xl border text-xs transition-all cursor-pointer ${
                      isActive
                        ? 'bg-emerald-600/20 border-emerald-500 text-white font-semibold'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
                    }`}
                  >
                    <span className="block truncate max-w-[130px] font-medium">{test.customerName.split(' ')[1] || test.customerName}</span>
                    <span className="block text-[10px] text-slate-500 truncate max-w-[130px]">{test.serviceCategory}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
